/**
 * This extension is a Firefox-specific workaround that prevents carets from being
 * drawn above placeholder text when the editor is empty. The strategy is to hide the
 * native caret when there is no content and let CodeMirror's drawn caret render in the
 * right spot.
 *
 * @see https://bugzilla.mozilla.org/show_bug.cgi?id=904846
 * @see https://bugzilla.mozilla.org/show_bug.cgi?id=2005674
 * @see https://github.com/w3c/editing/issues/501
 */

import {Prec, type Extension} from '@codemirror/state'
import {EditorView, layer, RectangleMarker} from '@codemirror/view'

const NATIVE_CARET_HIDDEN_CLASS = 'cm-firefox-empty-native-caret-hidden'
const DRAWN_CARET_CLASS = 'cm-firefox-empty-editor-caret'

function isFirefox() {
  return typeof navigator !== 'undefined' && navigator.userAgent.includes('Firefox')
}

function shouldDrawCursor(view: EditorView) {
  return view.hasFocus && view.state.doc.length === 0
}

// Based on https://github.com/codemirror/view/blob/main/src/draw-selection.ts.
const cursorLayer = layer({
  above: true,
  markers(view) {
    if (!shouldDrawCursor(view)) return []

    return view.state.selection.ranges.flatMap(range =>
      RectangleMarker.forRange(view, `cm-cursor cm-cursor-primary ${DRAWN_CARET_CLASS}`, range),
    )
  },
  update(update, dom) {
    if (update.transactions.some(transaction => transaction.selection)) {
      dom.style.animationName = dom.style.animationName === 'cm-blink' ? 'cm-blink2' : 'cm-blink'
    }

    return update.docChanged || update.selectionSet || update.focusChanged || update.viewportChanged
  },
  class: 'cm-cursorLayer',
})

const hideNativeCaretWhenEmpty = [
  EditorView.contentAttributes.of(view => (view.state.doc.length === 0 ? {class: NATIVE_CARET_HIDDEN_CLASS} : null)),
  Prec.highest(
    EditorView.theme({
      [`.cm-content.${NATIVE_CARET_HIDDEN_CLASS}`]: {
        caretColor: 'transparent !important',
      },
      [`.${DRAWN_CARET_CLASS}`]: {
        borderLeftColor: 'var(--codeMirror-cursor-fgColor, var(--color-codemirror-cursor))',
      },
    }),
  ),
]

export function firefoxEmptyEditorCaret(): Extension {
  return isFirefox() ? [cursorLayer, hideNativeCaretWhenEmpty] : []
}
